// dependencies
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";

import type {
	CreateReviewCycleRequestInput,
	CreateReviewCycleResponseJson,
	ExportReviewCycleReviewReportsDetailsResponseJson,
	GetReviewCycleDetailsResponseFrontend,
	GetReviewCyclePreviewSubjectsResultsRequestInput,
	GetReviewCyclePreviewSubjectsResultsResponseJson,
	PostReviewCyclePreviewSubjectsRequestInput,
	PostReviewCyclePreviewSubjectsResponseJson,
	ReviewCycleDetailsLegacyResponseFrontend,
	ReviewCycleEasyAddFormConfigJson,
	ReviewCycleFormSummary,
	ReviewCycleReportRowFrontend,
	ReviewDetailsFrontend,
	UpdateReviewCycleRequest,
	UpdateReviewCycleResponseJson,
} from "@performyard/service-contracts";

import {
	reviewCycleDetailsQuery,
	reviewCycleEasyAddFormConfigsQuery,
	reviewCycleFormsSummaryQuery,
	reviewCycleMetaQuery,
	reviewCycleReportsStreamQuery,
	reviewCycleReviewsDetailsStreamQuery,
	reviewCycleSubjectsQuery,
	reviewCyclesListQuery,
} from "../../queries";
import { queryClient } from "../../queryClient";
// api
import {
	type AddSubjectsToReviewCyclePayload,
	type GetReviewCyclesParams,
	addSubjectsToReviewCycle,
	bulkApproveEasyAddFormsForReviewCycle,
	bulkDeactivateEasyAddFormsForReviewCycle,
	closeReviewCycle,
	createReviewCycle,
	deleteReviewCycle,
	deleteReviewFromReviewCycle,
	downloadGoalDiscussionCsv,
	exportReviewCycleReviewReportsDetailsCsv,
	fetchReviewTemplateV2,
	getReviewCycleDetailsLegacy,
	getReviewCyclePreviewSubjectsResults,
	postReviewCyclePreviewSubjects,
	reviewCycleForms,
	updateReviewCycle,
} from "../../services";
import type { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

export const useReviewCycles = (params: GetReviewCyclesParams) => {
	return useQuery(reviewCyclesListQuery(params));
};

export const useReviewCycleForms = (reviewCycleID: string) => {
	return useQuery({
		queryKey: ["review-cycle-forms", reviewCycleID],
		queryFn: () => reviewCycleForms(reviewCycleID),
		meta: {
			errorMessage: "There was an error getting the review cycle forms.",
		},
		enabled: !!reviewCycleID,
	});
};

export const useReviewCycleDetailsLegacy = (
	reviewCycleID: string,
	options?: PYUseQueryOptions<ReviewCycleDetailsLegacyResponseFrontend>
) => {
	return useQuery<ReviewCycleDetailsLegacyResponseFrontend>({
		queryKey: ["review-cycle-details-legacy", reviewCycleID],
		queryFn: async () => await getReviewCycleDetailsLegacy(reviewCycleID),
		meta: {
			errorMessage: "There was an error getting the review cycle details.",
		},
		enabled: !!reviewCycleID,
		...options,
	});
};

export const useReviewCycleMeta = (reviewCycleID?: string) => {
	return useQuery(reviewCycleMetaQuery(reviewCycleID));
};

export const useReviewCycleSubjects = (reviewCycleId: string) => {
	return useQuery(reviewCycleSubjectsQuery(reviewCycleId));
};

export const useReviewCycleReviewsDetailsStream = (
	reviewCycleId: string,
	onChunk?: (chunk: { data: ReviewDetailsFrontend[] }) => void,
	options?: PYUseQueryOptions<ReviewDetailsFrontend[]>
) => {
	const resetQuery = () => {
		// Remove the cached data entirely to force fresh fetch (needed because of infinite staleTime)
		queryClient.removeQueries({ queryKey: reviewCycleReviewsDetailsStreamQuery(reviewCycleId).queryKey });
	};
	const query = useQuery<ReviewDetailsFrontend[]>({
		...reviewCycleReviewsDetailsStreamQuery(reviewCycleId, onChunk),
		...options,
	});

	return {
		...query,
		resetQuery,
	};
};

export const useReviewCycleReportsStream = (
	reviewCycleId: string,
	onChunk?: (chunk: { data: ReviewCycleReportRowFrontend[] }) => void,
	options?: PYUseQueryOptions<ReviewCycleReportRowFrontend[]>
) => {
	return useQuery<ReviewCycleReportRowFrontend[]>({
		...reviewCycleReportsStreamQuery(reviewCycleId, onChunk),
		...options,
	});
};

export const useDeleteReviewFromReviewCycle = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: async ({ reviewCycleId, reviewId }: { reviewCycleId: string; reviewId: string }) =>
			deleteReviewFromReviewCycle(reviewCycleId, reviewId),
		...options,
		onSuccess: (response, variables, context, mutation) => {
			// Manually update the query data without the removed review
			queryClient.setQueryData(
				reviewCycleReviewsDetailsStreamQuery(variables.reviewCycleId).queryKey,
				(old: ReviewDetailsFrontend[]) => old.filter((review) => review._id !== variables.reviewId)
			);
			queryClient.invalidateQueries({ queryKey: reviewCycleDetailsQuery(variables.reviewCycleId).queryKey });
			options?.onSuccess?.(response, variables, context, mutation);
		},
	});
};

export const useReviewCycleDetails = (
	reviewCycleId: string,
	options?: PYUseQueryOptions<GetReviewCycleDetailsResponseFrontend>
) => {
	return useQuery(reviewCycleDetailsQuery(reviewCycleId, options));
};

export const useReviewCycleFormsSummary = (
	reviewCycleId: string,
	options?: PYUseQueryOptions<ReviewCycleFormSummary[]>
) => {
	return useQuery<ReviewCycleFormSummary[]>(reviewCycleFormsSummaryQuery(reviewCycleId, options));
};

export const useReviewCycleEasyAddFormConfigs = (
	reviewCycleId: string,
	options?: PYUseQueryOptions<ReviewCycleEasyAddFormConfigJson[]>
) => {
	return useQuery<ReviewCycleEasyAddFormConfigJson[]>(reviewCycleEasyAddFormConfigsQuery(reviewCycleId, options));
};

export const useCloseReviewCycle = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: async ({ reviewCycleId }: { reviewCycleId: string }) => closeReviewCycle(reviewCycleId),
		...options,
		onSuccess: (response, variables, context, mutation) => {
			// Optimistically expect a job to appear on the WS shortly
			try {
				const { useReviewCycleStatusStore } = require("../../websocket/review-cycle-status");
				useReviewCycleStatusStore.getState().expectJobFor(2000);
			} catch (error) {
				console.error("Error optimistically expecting a job on the WS:", error);
			}
			// invalidate meta
			queryClient.invalidateQueries({ queryKey: reviewCycleDetailsQuery(variables.reviewCycleId).queryKey });
			// invalidate chunks - use a partial key to match all instances
			queryClient.invalidateQueries({
				queryKey: ["review-cycle-reviews-details", variables.reviewCycleId],
				exact: false,
			});
			options?.onSuccess?.(response, variables, context, mutation);
		},
	});
};

export const useBulkDeactivateEasyAddFormsForReviewCycle = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: async ({ reviewCycleId }: { reviewCycleId: string }) =>
			bulkDeactivateEasyAddFormsForReviewCycle(reviewCycleId),
		...options,
	});
};

export const useBulkApproveEasyAddFormsForReviewCycle = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: async ({ reviewCycleId }: { reviewCycleId: string }) =>
			bulkApproveEasyAddFormsForReviewCycle(reviewCycleId),
		...options,
	});
};

export const useDeleteReviewCycle = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: async ({ reviewCycleId }: { reviewCycleId: string }) => deleteReviewCycle(reviewCycleId),
		...options,
		onSuccess: (response, variables, context, mutation) => {
			options?.onSuccess?.(response, variables, context, mutation);
		},
	});
};

export const useDownloadGoalDiscussionCsv = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: async ({ reviewCycleId }: { reviewCycleId: string }) => downloadGoalDiscussionCsv(reviewCycleId),
		...options,
		onSuccess: (response, variables, context, mutation) => {
			options?.onSuccess?.(response, variables, context, mutation);
		},
	});
};

export const useReviewTemplateAssignments = (templateId?: string) => {
	return useQuery({
		queryKey: ["review-template-assignments", templateId],
		queryFn: () => fetchReviewTemplateV2(templateId as string),
		select: (template) => template.formAssignments,
		meta: {
			errorMessage: "There was an error getting the review template forms.",
		},
		enabled: !!templateId,
	});
};

export const useAddSubjectsToReviewCycle = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: async ({
			reviewCycleId,
			payload,
		}: {
			reviewCycleId: string;
			payload: AddSubjectsToReviewCyclePayload;
		}) => addSubjectsToReviewCycle(reviewCycleId, payload),
		...options,
	});
};

export const useExportReviewCycleReviewReportsDetailsCsv = (
	options?: PYUseMutationOptions<
		ExportReviewCycleReviewReportsDetailsResponseJson,
		Error,
		{ reviewCycleId: string; timezone: string }
	>
) => {
	return useMutation({
		mutationFn: async ({ reviewCycleId, timezone }) =>
			exportReviewCycleReviewReportsDetailsCsv(reviewCycleId, { timezone }),
		...options,
	});
};

export const useCreateReviewCycle = (
	options?: PYUseMutationOptions<CreateReviewCycleResponseJson, Error, CreateReviewCycleRequestInput>
) => {
	return useMutation({
		mutationFn: createReviewCycle,
		meta: {
			errorMessage: "There was an error creating the review cycle.",
		},
		...options,
	});
};

export const useUpdateReviewCycle = (
	options?: PYUseMutationOptions<
		UpdateReviewCycleResponseJson,
		Error,
		{ reviewCycleId: string; data: UpdateReviewCycleRequest }
	>
) => {
	return useMutation({
		mutationFn: ({ reviewCycleId, data }: { reviewCycleId: string; data: UpdateReviewCycleRequest }) =>
			updateReviewCycle(reviewCycleId, data),
		meta: {
			errorMessage: "There was an error updating the review cycle.",
		},
		...options,
		onSuccess: (response, variables, context, mutation) => {
			queryClient.invalidateQueries({ queryKey: reviewCycleDetailsQuery(variables.reviewCycleId).queryKey });
			queryClient.invalidateQueries({ queryKey: reviewCycleMetaQuery(variables.reviewCycleId).queryKey });
			options?.onSuccess?.(response, variables, context, mutation);
		},
	});
};

export const useCreateReviewCyclePreviewSubjects = (
	options?: PYUseMutationOptions<
		PostReviewCyclePreviewSubjectsResponseJson,
		Error,
		PostReviewCyclePreviewSubjectsRequestInput
	>
) => {
	return useMutation({
		mutationFn: postReviewCyclePreviewSubjects,
		meta: {
			errorMessage: "There was an error generating the qualifying subjects preview.",
		},
		...options,
	});
};

export const useReviewCyclePreviewSubjectsResults = (
	{
		previewId,
		params,
	}: {
		previewId: string | null;
		params: GetReviewCyclePreviewSubjectsResultsRequestInput;
	},
	options?: PYUseQueryOptions<GetReviewCyclePreviewSubjectsResultsResponseJson>
) => {
	return useQuery<GetReviewCyclePreviewSubjectsResultsResponseJson>({
		queryKey: ["review-cycle-preview-subjects-results", previewId, params],
		queryFn: () => getReviewCyclePreviewSubjectsResults(previewId!, params),
		meta: {
			errorMessage: "There was an error fetching the qualifying subjects.",
		},
		enabled: !!previewId,
		placeholderData: keepPreviousData,
		...options,
	});
};
